import { useCallback } from "react";
import usePageLanguage from "./usePageLanguage.js";
import { localizedPath } from "../content/pages/registry.js";

/**
 * Resolve an internal href for the language of the page it is rendered on.
 *
 * The shared Navbar and Footer are the same components in both language trees,
 * and their links were written as literal English paths — which was correct
 * while English was the only tree there was. On a Greek page those links now
 * have to reach the Greek pages, and a visitor who follows one must not be
 * quietly dropped back into English halfway through a session.
 *
 * The same applies to every link inside the copy — the homepage's capability
 * hand-offs, each product page's cross-links and plan notes, the cookie
 * banner's privacy link. Those were localized later than the navigation was,
 * and the gap was invisible because the STATIC documents never had it:
 * `localizeHrefs` (scripts/lib/localizeDocument.mjs) resolves every href as it
 * renders them, so the crawlable Greek page linked to Greek pages while the
 * rendered one sent the reader to English. `languageRouting.test.mjs` now
 * sweeps every component for a literal path that has a Greek counterpart.
 *
 * The decision itself is not made here. `localizedPath` makes it, from the page
 * registry, which is what gives internal links the one behaviour that matters:
 *
 *   /wedding-rsvp  on a Greek page → /el/wedding-rsvp    (a Greek page exists)
 *   /contact       on a Greek page → /contact            (none exists yet)
 *
 * Falling back to the English URL — rather than prefixing every path and hoping
 * — is what keeps a Greek page from linking to `/el/contact`, which would be a
 * self-inflicted 404 on a page a visitor was actively trying to reach. When
 * /contact gains a Greek version, every link to it becomes Greek with no edit
 * here: the registry is the only thing that has to learn about it.
 *
 * Fragments and external URLs pass through untouched — `#pricing` is a section
 * of the current document, and an `https://` or `mailto:` link is not ours to
 * localize.
 */
export default function useLocalizedHref() {
  const language = usePageLanguage();

  return useCallback(
    (href) => {
      if (typeof href !== "string" || !href.startsWith("/")) return href;
      const [path, fragment] = href.split("#");
      const resolved = localizedPath(path || "/", language);
      return fragment === undefined ? resolved : `${resolved}#${fragment}`;
    },
    [language],
  );
}
